// eslint-disable-next-line max-classes-per-file
const ajax = require('component-ajax');
const q = require('queried');
const cookie = require('cookie-cutter');
const forEach = require('object-foreach');
const find = require('lodash/find');
const extend = require('xtend/mutable');
const emit = require('emmy/emit');
const _ = require('./i18n');
const {
  showLoginPopup,
  getIcon,
} = require('./utils');

function getIconHTML(name) {
  return `&#x${window.kudago.icons[name]};`;
}

class Like {
  constructor({ element }) {
    this.element = element;
    this.number = q('.js-likes-number', element);
    this.type = element.getAttribute('data-type');
    this.id = element.getAttribute('data-id');
  }

  toggle() {
    if (this.isLoggedIn()) {
      if (this.isActive()) {
        this.deactivate();
      } else {
        this.activate();
      }
    } else {
      this.setLikeCookie();
      showLoginPopup({
        reason: 'like',
      });
    }
  }

  isLoggedIn() {
    return window.kudago.user && window.kudago.user.isAuthenticated;
  }

  isActive() {
    return !!this.element.getAttribute('data-active');
  }

  sendRequest(url) {
    this.ajax({
      type: 'POST',
      url,
      headers: {
        'X-CSRFToken': this.cookie.get('csrftoken'),
      },
      data: {
        type: this.type,
        id: this.id,
      },
    });
  }

  setLikeCookie() {
    this.cookie.set('dfdfav', `${this.type}-${this.id}`, {
      expires: new Date(Date.now() + 1000 * 60 * 60),
      path: '/',
    });
  }

  activate() {
    const items = this.getBrothers();
    const likesNumber = this.getLikesNumber();

    items.forEach((item) => {
      item.setAttribute('data-active', true);
      item.setAttribute('title', this._('I don’t like it anymore'));

      const heart = q('i', item);
      const numberElement = q('.js-likes-number', item);

      if (heart) {
        heart.classList.add('icon-heart');
        heart.classList.remove('icon-heart-empty');
        heart.innerHTML = getIconHTML('heart');

        heart.classList.remove('heartbeat-out');
        // timeout is needed for css animations
        setImmediate(() => heart.classList.add('heartbeat-in'));
      }

      Like.setLikesNumber(numberElement, likesNumber + 1);
      Like.updateNumberSize(numberElement, likesNumber + 1);
    });

    this.sendRequest('/favorites/add/');
  }

  deactivate() {
    const items = this.getBrothers();
    const likesNumber = this.getLikesNumber();

    items.forEach((item) => {
      item.removeAttribute('data-active', true);
      item.setAttribute('title', this._('I like it'));

      const heart = q('i', item);
      const numberElement = q('.js-likes-number', item);

      if (heart) {
        heart.classList.add('icon-heart-empty');
        heart.classList.remove('icon-heart');
        heart.innerHTML = getIconHTML('heart-empty');

        heart.classList.remove('heartbeat-in');
        // timeout is needed for css animations
        setImmediate(() => heart.classList.add('heartbeat-out'));
      }

      const newLikesNumber = likesNumber - 1;
      if (newLikesNumber) {
        Like.setLikesNumber(numberElement, likesNumber - 1);
        Like.updateNumberSize(numberElement, likesNumber - 1);
      } else {
        numberElement.textContent = '';
      }
    });

    this.sendRequest('/favorites/remove/');
  }

  static updateNumberSize(element, number) {
    element.className = element.className.replace(/\w+-digits?-size\s?/g, '');

    if (number < 10) {
      element.classList.add('one-digit-size');
    } else if (number < 100) {
      element.classList.add('two-digits-size');
    } else {
      element.classList.add('three-digits-size');
    }
  }

  static setLikesNumber(element, number) {
    if (!element.classList.contains('js-likes-number-labeled')) {
      element.textContent = number;
    } else {
      const format = Like._.ngettext('%s likes', '%s likes', number);
      element.textContent = this._.interpolate(format, [number]);
    }
  }

  getBrothers() {
    return q.all(`.js-likes[data-type="${this.type}"][data-id="${this.id}"]`);
  }

  getLikesNumber() {
    return parseInt(this.number.textContent, 10) || 0;
  }
}

// prototype and class properties
// useful for testing
Like._ = _;
extend(Like.prototype, {
  _, ajax, cookie,
});

class DynamicLike {
  constructor(element) {
    this.element = element;
    this.id = Number(element.getAttribute('data-id'));
    this.typeId = Number(element.getAttribute('data-type'));
    this.typeName = element.getAttribute('data-type-name');
    this.classnames = element.getAttribute('data-classnames') || '';

    this.fetchData();
  }

  fetchData() {
    this.ajax({
      type: 'GET',
      url: '/favorites/count/',
      data: {
        object: `${this.typeId},${this.id}`,
      },
      success: (response) => {
        this.render(this.getResponseData(response));
        emit(document, 'likes-loaded');
      },
    });
  }

  getResponseData(input) {
    const data = find(input, { object_id: this.id, content_type_id: this.typeId });

    if (!data) {
      // if item has 0 likes API doesn't return object data,
      // but, to be honest, in that case it's pretty easy for us
      // to make the data by ourselves ;)
      return {
        object_id: this.id,
        content_type_id: this.typeId,
        favorites: 0,
        user_likes: false,
      };
    }

    return data;
  }

  getAttributes(isLiked) {
    const attrs = {
      'data-id': this.id,
      'data-type': this.typeName,
    };

    if (window.kudago.user.isAuthenticated) {
      if (isLiked) {
        attrs.title = _('I don’t like it anymore');
        attrs['data-active'] = true;
      } else {
        attrs.title = _('I like it');
      }
    } else {
      attrs['data-disabled'] = true;
    }

    let attrsString = '';
    forEach(attrs, (value, name) => {
      attrsString += `${name}="${value}" `;
    });

    return attrsString;
  }

  render(data) {
    const isLiked = !!data.user_likes;
    const count = data.favorites;
    let countClasses = '';
    if (this.classnames.includes('post-fav')) {
      countClasses = `post-fav-number ${DynamicLike.getSizeClass(count)}`;
    }

    this.element.innerHTML = `
      <div class="js-likes ${this.classnames}" ${this.getAttributes(isLiked)}
      >
          <div class="post-fav-hearts">
              ${getIcon(isLiked ? 'heart' : 'heart-empty')}
          </div>
          <span class="${countClasses} js-likes-number">
              ${count || ''}
          </span>
      </div>
    `;
  }

  static getSizeClass(count) {
    if (count < 10) {
      return 'one-digit-size';
    } if (count < 100) {
      return 'two-digits-size';
    }

    return 'three-digits-size';
  }
}

// useful for testing
extend(DynamicLike.prototype, { ajax });

module.exports = {
  Like,
  DynamicLike,
};
